import { useEffect, useState } from 'react';

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  'https://us-central1-kindkudos-prod.cloudfunctions.net';
const TOKEN_KEY = 'kindkudos_admin_token';

async function adminFetch(token, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_BASE}/kudosAdmin${qs ? `?${qs}` : ''}`, {
    headers: { 'x-admin-token': token },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((data && data.error) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

function fmtDate(ms) {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString();
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openChain, setOpenChain] = useState(null); // { rootId, data, loading, error }

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    adminFetch(token, { action: 'overview' })
      .then((data) => {
        setOverview(data);
        setAuthed(true);
        localStorage.setItem(TOKEN_KEY, token);
      })
      .catch((err) => {
        setAuthed(false);
        setError(err.status === 401 ? 'Invalid token.' : err.message);
        if (err.status === 401) localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setLoading(false));
  }, [token]);

  function handleLogin(e) {
    e.preventDefault();
    const value = e.target.token.value.trim();
    if (value) setToken(value);
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setOverview(null);
  }

  async function loadChain(rootId) {
    if (openChain?.rootId === rootId) {
      setOpenChain(null);
      return;
    }
    setOpenChain({ rootId, loading: true });
    try {
      const data = await adminFetch(token, { action: 'chain', rootId });
      setOpenChain({ rootId, data });
    } catch (err) {
      setOpenChain({ rootId, error: err.message });
    }
  }

  if (!authed) {
    return (
      <section className="mt-10 max-w-md mx-auto">
        <div className="card">
          <h1 className="text-2xl font-display font-bold mb-4">Admin</h1>
          <form onSubmit={handleLogin} className="space-y-3">
            <label htmlFor="token" className="label">
              Admin token
            </label>
            <input
              id="token"
              name="token"
              type="password"
              className="input"
              placeholder="Paste your admin token"
              defaultValue={token}
              autoFocus
            />
            <button type="submit" className="btn-primary w-full">
              Sign in
            </button>
            {error && (
              <p className="text-sm text-orange-600" role="alert">
                {error}
              </p>
            )}
            {loading && <p className="text-sm text-navy-500">Checking&hellip;</p>}
          </form>
        </div>
      </section>
    );
  }

  if (loading || !overview) {
    return <p className="mt-10 text-center text-navy-700">Loading admin data&hellip;</p>;
  }

  const { stats, topChains, recent } = overview;

  return (
    <section className="mt-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-display font-bold text-navy">Admin</h1>
        <button onClick={logout} className="text-sm text-navy-500 underline">
          Sign out
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Today" value={stats.today} />
        <Stat label="All time" value={stats.allTime} />
        <Stat label="Top chains" value={topChains.length} />
        <Stat label="UTC date" value={stats.dateUtc} small />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Blocked today" value={stats.rejectedToday ?? 0} />
        <Stat label="Blocked all time" value={stats.rejectedAllTime ?? 0} />
        <Stat
          label="Block rate today"
          value={
            (stats.today + (stats.rejectedToday ?? 0)) > 0
              ? `${Math.round(((stats.rejectedToday ?? 0) / (stats.today + (stats.rejectedToday ?? 0))) * 100)}%`
              : '—'
          }
          small
        />
        <Stat
          label="Total attempts today"
          value={stats.today + (stats.rejectedToday ?? 0)}
        />
      </div>

      <div className="card">
        <h2 className="text-xl font-display font-bold mb-3">Top chains (by reach)</h2>
        {topChains.length === 0 ? (
          <p className="text-sm text-navy-500">No chains yet.</p>
        ) : (
          <div className="space-y-2">
            {topChains.map((c) => (
              <div key={c.rootKudosId} className="border-b border-navy-100 pb-2">
                <button
                  onClick={() => loadChain(c.rootKudosId)}
                  className="w-full text-left flex items-center justify-between hover:bg-orange-50/40 px-2 py-1 rounded"
                >
                  <span className="font-mono text-xs text-navy-500 truncate max-w-xs">
                    {c.rootKudosId}
                  </span>
                  <span className="text-sm text-navy-700">
                    <strong>{c.totalCount}</strong> people · depth {c.maxDepth} ·{' '}
                    last activity {fmtDate(c.lastUpdateAt)}
                  </span>
                </button>
                {openChain?.rootId === c.rootKudosId && (
                  <ChainDetail openChain={openChain} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-xl font-display font-bold mb-3">Recent kudos (last 25)</h2>
        <div className="space-y-3">
          {recent.map((r) => (
            <div key={r.id} className="border-b border-navy-100 pb-2 last:border-b-0">
              <div className="flex justify-between text-xs text-navy-500 mb-1">
                <span>
                  {fmtDate(r.createdAt)} · #{r.chainPosition} ·{' '}
                  <span className="font-mono">{r.id.slice(0, 8)}…</span>
                </span>
                <span className={r.delivered ? 'text-teal-700' : 'text-orange-600'}>
                  {r.delivered ? 'delivered' : 'undelivered'}
                </span>
              </div>
              <div className="text-sm text-navy-700">
                <strong>To {r.recipientName}</strong>{' '}
                <span className="text-navy-500">&lt;{r.recipientEmail}&gt;</span>
              </div>
              <p className="text-sm text-navy-900 mt-1 whitespace-pre-wrap">
                &ldquo;{r.message}&rdquo;
              </p>
              <button
                onClick={() => loadChain(r.rootKudosId)}
                className="text-xs text-teal-700 mt-1"
              >
                View chain →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="card text-center py-3">
      <div className="text-xs uppercase tracking-wider text-navy-500">{label}</div>
      <div className={small ? 'text-sm font-medium text-navy mt-1' : 'text-2xl font-bold text-navy mt-1'}>
        {value}
      </div>
    </div>
  );
}

function ChainDetail({ openChain }) {
  if (openChain.loading) {
    return <p className="text-xs text-navy-500 px-2 py-1">Loading chain&hellip;</p>;
  }
  if (openChain.error) {
    return <p className="text-xs text-orange-600 px-2 py-1">{openChain.error}</p>;
  }
  const { data } = openChain;
  const nodes = data.nodes || [];

  // Build parent -> children map. Roots are nodes whose parentKudosId is null
  // OR whose parent is not in this result set (defensive).
  const byParent = new Map();
  const ids = new Set(nodes.map((n) => n.id));
  const roots = [];
  for (const n of nodes) {
    const pid = n.parentKudosId && ids.has(n.parentKudosId) ? n.parentKudosId : null;
    if (pid === null) roots.push(n);
    else {
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(n);
    }
  }

  // Sort children chronologically so the tree reads top-to-bottom in send order.
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  }
  roots.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

  // Depth fan-out summary: "1 → 3 → 9 → 27"
  const byDepth = nodes.reduce((acc, n) => {
    const d = n.depth ?? 0;
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const depths = Object.keys(byDepth)
    .map(Number)
    .sort((a, b) => a - b);
  const fanout = depths.map((d) => byDepth[d]).join(' → ');

  return (
    <div className="mt-2 pl-4 border-l-2 border-teal-300 space-y-3">
      <div className="text-xs text-navy-500">
        Branching: <span className="font-mono text-navy-700">{fanout || '0'}</span>{' '}
        ({nodes.length} total · max depth {data.maxDepth ?? 0})
      </div>
      {roots.map((r) => (
        <ChainNode key={r.id} node={r} byParent={byParent} level={0} />
      ))}
    </div>
  );
}

function ChainNode({ node, byParent, level }) {
  const children = byParent.get(node.id) || [];
  const childCount = children.length;
  return (
    <div className="text-sm" style={{ marginLeft: level * 16 }}>
      <div className="border-l-2 border-orange-200 pl-3 py-1">
        <div className="text-xs text-navy-500">
          #{node.chainPosition} · depth {node.depth} · {fmtDate(node.createdAt)} ·{' '}
          <span className={node.delivered ? 'text-teal-700' : 'text-orange-600'}>
            {node.delivered ? 'delivered' : 'undelivered'}
          </span>
          {childCount > 0 && (
            <span className="ml-2 inline-block rounded-full bg-teal-100 text-teal-800 px-2 py-0.5 text-[10px] font-medium">
              {childCount} {childCount === 1 ? 'fork' : 'forks'}
            </span>
          )}
        </div>
        <div className="text-navy-700">
          To <strong>{node.recipientName}</strong>{' '}
          <span className="text-navy-500">&lt;{node.recipientEmail}&gt;</span>
        </div>
        <p className="text-navy-900 whitespace-pre-wrap">&ldquo;{node.message}&rdquo;</p>
      </div>
      {children.length > 0 && (
        <div className="mt-2 space-y-2">
          {children.map((c) => (
            <ChainNode key={c.id} node={c} byParent={byParent} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
